import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ptBR from "./locales/pt-BR.json";
import en from "./locales/en.json";
import es from "./locales/es.json";

export const SUPPORTED_LOCALES = [
  { code: "pt-BR", label: "Português", short: "PT" },
  { code: "en", label: "English", short: "EN" },
  { code: "es", label: "Español", short: "ES" },
];

export const DEFAULT_LOCALE = "pt-BR";
export const STORAGE_KEY = "eliabe-jeans-language";

// Map any browser locale to one of our 3 supported locales.
export const normalizeLocale = (raw) => {
  if (!raw || typeof raw !== "string") return DEFAULT_LOCALE;
  const lower = raw.toLowerCase();
  if (lower.startsWith("pt")) return "pt-BR";
  if (lower.startsWith("en")) return "en";
  if (lower.startsWith("es")) return "es";
  return DEFAULT_LOCALE;
};

// Detect the best initial locale honoring:
// 1) manually saved preference in localStorage
// 2) navigator.languages (in order)
// 3) navigator.language
// 4) pt-BR fallback
const detectInitialLocale = () => {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const normalized = normalizeLocale(saved);
      // Only trust saved value if it maps to a supported code exactly.
      if (SUPPORTED_LOCALES.some((l) => l.code === saved)) return saved;
      return normalized;
    }
  } catch (_) {
    /* ignore storage errors */
  }

  const nav = window.navigator || {};
  const list =
    (Array.isArray(nav.languages) && nav.languages.length ? nav.languages : [nav.language]) || [];

  for (const candidate of list) {
    if (!candidate) continue;
    const lower = String(candidate).toLowerCase();
    if (lower.startsWith("pt")) return "pt-BR";
    if (lower.startsWith("en")) return "en";
    if (lower.startsWith("es")) return "es";
  }
  return DEFAULT_LOCALE;
};

const initialLocale = detectInitialLocale();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      "pt-BR": { translation: ptBR },
      en: { translation: en },
      es: { translation: es },
    },
    lng: initialLocale,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: ["pt-BR", "en", "es"],
    interpolation: { escapeValue: false },
    returnNull: false,
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

// Keep <html lang> in sync with active language.
const applyHtmlLang = (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", lng || DEFAULT_LOCALE);
  }
};
applyHtmlLang(i18n.language);
i18n.on("languageChanged", applyHtmlLang);

export default i18n;
